import {
  ListCard,
  Badge,
  markdownPlainTextPreview,
  normalizeSkillKey,
  cn,
  ListCardTrailingActionCluster,
  MatchBandBadge,
  FitInsightPreview,
} from '@yeshuman/sdk'
import type {
  ListCardHeaderBadge,
  FitInsightPreviewProps,
  CandidateMatchBand,
  ResolvedListingProvider,
} from '@yeshuman/sdk'
import { memo, type ReactNode } from 'react'
import {
  JobOpeningIdentityHeader,
  JobOpeningMetaLine,
  JobOpeningSalaryFooter,
  hasJobOpeningMetaContent,
  parseJobOpeningMetaFields,
} from './job-opening-identity'

import { JobBoardSaveButton } from './candidate/job-board-save-button'
import type { OpportunityJobSummaryInput } from '../utils/opportunity-job-meta'

export type JobCardSkill = {
  id: string
  name: string
}

const MAX_VISIBLE_SKILLS = 3
const EMPTY_MATCHED_SKILL_KEYS: string[] = []

export type JobCardProps = {
  title: string
  publicationStatus?: string | null
  /**
   * `standard` — full body (meta line, description, skills, fit insight).
   * `compact` — metadata-only teaser: no posted date, description, or skills.
   */
  variant?: 'standard' | 'compact'
  /** When false (e.g. candidate browse), omit Draft/Open/Closed badge. Default true. */
  showPublicationBadge?: boolean
  /** When `showPublicationBadge` is false, optional Featured/New-style badge. */
  promotionBadge?: ListCardHeaderBadge | null
  /** First publish time (ISO); shown in the meta line when set. */
  publishedAt?: string | null
  /** Last update (ISO); shown when `publishedAt` is absent. */
  updatedAt?: string | null
  /** Saved bookmark state for candidate job-board cards. */
  saved?: boolean
  onSaveToggle?: () => void
  isSaving?: boolean
  /** Selected browse skill keys that match this opportunity's skills. */
  matchedSkillKeys?: string[]
  description?: string | null
  meta?: OpportunityJobSummaryInput | null
  organisationLabel?: string | null
  organisationLogoUrl?: string | null
  skills?: JobCardSkill[]
  onOpen?: () => void
  openAriaLabel?: string
  footerNote?: string | null
  className?: string
  trailing?: ReactNode
  /** Listing source (Adzuna, TalentCo platform, …) — shown below org name. */
  listingProvider?: ResolvedListingProvider | null
  matchBand?: CandidateMatchBand
  matchBandCaption?: string | null
  fitInsight?: Pick<
    FitInsightPreviewProps,
    | 'highlights'
    | 'redFlags'
    | 'caption'
    | 'recommendation'
    | 'onShowDetails'
    | 'pending'
    | 'notAnalysed'
    | 'stuck'
    | 'onRetry'
    | 'retryPending'
    | 'variant'
  >
  unavailable?: boolean
  unavailableCaption?: string | null
}

/**
 * Job / opportunity row card for employer and candidate list surfaces.
 */
export const JobCard = memo(function JobCard({
  title,
  publicationStatus,
  variant = 'standard',
  showPublicationBadge = true,
  promotionBadge = null,
  publishedAt,
  updatedAt,
  saved = false,
  onSaveToggle,
  isSaving = false,
  matchedSkillKeys = EMPTY_MATCHED_SKILL_KEYS,
  description,
  meta,
  organisationLabel,
  organisationLogoUrl,
  skills = [],
  onOpen,
  openAriaLabel,
  footerNote,
  className,
  trailing,
  listingProvider,
  matchBand,
  matchBandCaption,
  fitInsight,
  unavailable = false,
  unavailableCaption = 'No longer available',
}: JobCardProps) {
  const trimmedDescription = markdownPlainTextPreview(description, [title])
  const compact = variant === 'compact'
  const visible = skills.slice(0, MAX_VISIBLE_SKILLS)
  const overflow = Math.max(0, skills.length - visible.length)
  const matchedKeys = new Set(matchedSkillKeys.map(normalizeSkillKey))
  const showSaveControl = onSaveToggle != null
  const hasFooterActions = showSaveControl || (trailing != null && !unavailable)

  const saveButton = showSaveControl ? (
    <JobBoardSaveButton
      saved={saved}
      disabled={isSaving}
      onClick={(event) => {
        event.stopPropagation()
        onSaveToggle()
      }}
    />
  ) : null

  const header = (
    <JobOpeningIdentityHeader
      title={title}
      publicationStatus={publicationStatus}
      showPublicationBadge={showPublicationBadge}
      promotionBadge={promotionBadge}
      organisationLabel={organisationLabel}
      organisationLogoUrl={organisationLogoUrl}
      listingProvider={listingProvider}
      trailing={
        matchBand !== undefined || matchBandCaption ? (
          <span className="inline-flex items-center gap-1.5">
            <MatchBandBadge band={matchBand ?? null} />
            {matchBandCaption ? (
              <span className="text-xs tabular-nums text-muted-foreground">{matchBandCaption}</span>
            ) : null}
          </span>
        ) : null
      }
    />
  )

  const showTimestamp = !compact
  const parsedMeta = parseJobOpeningMetaFields(meta)
  const { locationText, workMode, locationWorkModeText, employmentType, salaryText } = parsedMeta
  const hasMetaLine = hasJobOpeningMetaContent(meta, {
    showTimestamp,
    publishedAt,
    updatedAt,
    parsed: { locationWorkModeText, employmentType },
  })

  const metaLine = hasMetaLine ? (
    <JobOpeningMetaLine
      locationText={locationText}
      workMode={workMode}
      employmentType={employmentType}
      publishedAt={publishedAt}
      updatedAt={updatedAt}
      showTimestamp={showTimestamp}
    />
  ) : null

  const showStandardBody =
    hasMetaLine || Boolean(trimmedDescription) || skills.length > 0 || fitInsight || unavailable

  const body = compact
    ? metaLine
    : showStandardBody
      ? (
          <div className="flex min-w-0 flex-col gap-3">
            {metaLine}
            {fitInsight ? (
              <FitInsightPreview
                band={matchBand ?? null}
                highlights={fitInsight.highlights}
                redFlags={fitInsight.redFlags}
                caption={fitInsight.caption}
                recommendation={fitInsight.recommendation}
                pending={fitInsight.pending}
                notAnalysed={fitInsight.notAnalysed}
                stuck={fitInsight.stuck}
                onRetry={fitInsight.onRetry}
                retryPending={fitInsight.retryPending}
                onShowDetails={fitInsight.onShowDetails}
                variant={fitInsight.variant}
              />
            ) : null}
            {trimmedDescription ? (
              <p className="line-clamp-2 text-sm text-muted-foreground">{trimmedDescription}</p>
            ) : null}
            {skills.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {visible.map((skill) => {
                  const matched = matchedKeys.has(normalizeSkillKey(skill.name))
                  return (
                    <Badge key={skill.id} variant={matched ? 'defaultSoft' : 'secondarySoft'}>
                      {skill.name}
                    </Badge>
                  )
                })}
                {overflow > 0 ? (
                  <Badge variant="secondarySoft">
                    +{overflow} more
                  </Badge>
                ) : null}
              </div>
            ) : null}
            {unavailable ? (
              <p className="text-sm text-muted-foreground">{unavailableCaption}</p>
            ) : null}
          </div>
        )
      : null

  const hasFooterLeft = Boolean(salaryText || footerNote?.trim())
  const showFooter = hasFooterLeft || hasFooterActions

  const footer = showFooter ? (
    <JobOpeningSalaryFooter
      salaryLabel={salaryText}
      footerNote={footerNote}
      trailing={
        hasFooterActions ? (
          <ListCardTrailingActionCluster>
            {saveButton}
            {trailing}
          </ListCardTrailingActionCluster>
        ) : null
      }
    />
  ) : null

  return (
    <ListCard
      header={header}
      body={body}
      footer={footer}
      onOpen={onOpen}
      openAriaLabel={openAriaLabel ?? `Open job ${title}`}
      className={cn(className, unavailable && 'opacity-70')}
      footerClassName={
        showFooter
          ? cn('pointer-events-auto px-0', hasFooterLeft && 'border-t border-border/60')
          : undefined
      }
    />
  )
})
